import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PageShell } from '@/components/layout/PageShell'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { showApiError } from '@/lib/utils/notifications'
import { getServiceAgreementById } from '../services/documentManagementService'
import type { ServiceAgreementDocument } from '../types/document'
import { cn } from '@/lib/utils/helpers'
import { secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { ServiceAgreementHtml } from '../components/ServiceAgreementHtml'

function formatDate(value: string | undefined): string {
  if (!value) return '—'
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return value
  return `${match[2]}/${match[3]}/${match[1]}`
}

function coerceActive(value: ServiceAgreementDocument['is_active']): boolean {
  if (typeof value === 'boolean') return value
  return String(value) === '1' || String(value).toLowerCase() === 'true'
}

export function ServiceAgreementViewPage() {
  const { documentId } = useParams<{ documentId: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [document, setDocument] = useState<ServiceAgreementDocument | null>(null)

  const numericId = Number(documentId)

  useEffect(() => {
    if (!numericId) {
      navigate('/gdpr-consent/gdpr-document', { replace: true })
      return
    }

    let active = true
    const load = async () => {
      setLoading(true)
      try {
        const detail = await getServiceAgreementById(numericId)
        if (!active) return
        setDocument(detail)
      } catch (error) {
        // Preserve API body (Angular `notify(error.error || 'Document not found', 0)`).
        showApiError(error)
        if (active) navigate('/gdpr-consent/gdpr-document', { replace: true })
      } finally {
        if (active) setLoading(false)
      }
    }

    void load()
    return () => {
      active = false
    }
  }, [navigate, numericId])

  const isActive = useMemo(
    () => (document ? coerceActive(document.is_active) : false),
    [document],
  )

  const goBack = useCallback(() => {
    navigate('/gdpr-consent/gdpr-document')
  }, [navigate])

  return (
    <PageShell title="VIEW SERVICE AGREEMENT">
      {loading ? (
        <ModuleScreenLoading variant="inline" data-testid="service-agreement-view-loading" />
      ) : document ? (
        <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-4 border-b border-slate-200 pb-4 md:grid-cols-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Version</p>
              <p className="mt-1 text-sm font-medium text-slate-900">{document.version || '—'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Document Name</p>
              <p className="mt-1 text-sm font-medium text-slate-900">{document.title || '—'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Effective Dates
              </p>
              <p className="mt-1 text-sm font-medium text-slate-900">
                {formatDate(document.effective_start_date)} to {formatDate(document.effective_end_date)}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</p>
              <p
                className={cn(
                  'mt-1 inline-flex items-center gap-1.5 text-sm font-medium',
                  isActive ? 'text-emerald-700' : 'text-slate-900',
                )}
              >
                {isActive ? (
                  <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" aria-hidden />
                ) : null}
                {isActive ? 'Active' : 'Inactive'}
              </p>
            </div>
          </div>

          <div>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">Content</h2>
            <ServiceAgreementHtml
              html={document.content_draft || document.content}
              documentId={document.id ?? numericId}
              eorpSection={document.eorp_section}
              className="max-w-none rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-800"
              emptyFallback={
                <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                  No content available.
                </div>
              }
            />
          </div>

          <div className="border-t border-slate-200 pt-3">
            <button
              type="button"
              onClick={goBack}
              className={cn(secondaryEntityActionButtonClass, 'px-4 py-2')}
            >
              Back to Service agreement
            </button>
          </div>
        </div>
      ) : null}
    </PageShell>
  )
}
